export default {
  key: 'system--workspace-project',
  // Keep in sync with the page dispatch table in
  // packag./pages/index.js. bootShell._singleAppDispatch
  // (shared/functions/bootShell.js:78) silently history.replaceState()s
  // any path that doesn't match one of these regexes back to
  // fallbackRoute ('/'), so a missing entry presents as "URL flashes
  // /admin/usage then snaps to /" and the home page renders instead.
  routes: [
    /^\/$/,
    // Auth routes — keep in sync with functions/auth.js AUTH_PATHS (importing
    // it here would pull auth.js into the eager boot manifest). forgot-password
    // + verify-email were missing, so bootShell replaceState'd those two auth
    // pages to '/' before they could render.
    /^\/(login|signin|signup|signup-confirmation|reset-password|forgot-password|verify-email)(\/|$)/,
    /^\/auth\/callback(\/|\?|$)/,
    /^\/auth\/magic-callback(\/|\?|#|$)/,
    /^\/github-auth-callback(\/|\?|$)/,
    // Emailed invitation landings (P0 invite-flow fix, 2026-08-06) — keep in
    // sync with pages/index.js accept routes + guestRoutes.js. Without these
    // a single-app boot replaceStates the ?token= link back to '/'.
    /^\/(accept-invite|accept-org-invite|accept-team-invite|accept-workspace-invite|waitlist-invite)(\/|\?|$)/,
    /^\/(tickets|chat|chats|mail|terminal|calendar|inbox|epics|teams|meet|meet-guest|documents|docs)(\/|$)/,
    /^\/(admin|people|my|announcements|company|settings|profile|data|system-status|integrations|features|reminders|bookmarks|history|files|_session-bridge)(\/|$)/,
    /^\/(user|u)\/[^/]+/
  ],
  // The namespaces this shell's surfaces ACTUALLY read. It said
  // `['workspace-project']` until 2026-09-02 — a bundle nobody has written and
  // nobody plans: bootShell intersects a manifest's declaration against the
  // loader registry (packages/shared/functions/i18nLoader.js, derived from
  // packages/shared/i18n/ by scripts/gen-i18n-registry.mjs), so the whole
  // declaration was dropped, in silence, and the shell loaded `common` alone in
  // every non-English language. Measured on the GITA workspace: a member with
  // the workspace default `ka` got a correctly-resolved Georgian session with
  // 50 keys of `common` and an English page. bootShell now WARNS on a dropped
  // namespace instead of swallowing it.
  //
  // The three below are counted, not guessed — every key of each bundle grepped
  // against this package's and @symbo-ls/workspace-shared's sources:
  //   common   — 8 keys (action/state verbs in the shared chrome)
  //   account  — 11 keys (the profile surface, which mounts inside this shell)
  //   settings — 8 keys (the language section; `settings.language.*` renders on
  //              /admin/*, which the deleted path-prefix regex never matched —
  //              a Georgian admin read raw dotted keys across that surface)
  // `auth` is added by bootShell's guest branch, which is a STATE condition (no
  // user resolved yet), not a route guess — so it is not listed here.
  // `canvas` (606 keys) belongs to packages/canvas and is declared there.
  //
  // The routes array above already covers `/admin/*`, so every admin page is a
  // route of THIS app and needs no declaration of its own. That is the whole
  // mechanism now: a surface declares, nothing infers from a URL.
  // `workspace` / `admin` / `data` were added by LANE EXTRACT-WORKSPACE
  // (2026-09-02): the shell's own three surfaces, split by the route tree a
  // reader is actually on — the home board + app-window chrome (`workspace`),
  // the /admin console (`admin`), and /data + /data/external (`data`). One
  // manifest serves all of them because every one of those routes is a route
  // of THIS app (see the `routes` array above), so bootShell loads the three
  // together; the split is what keeps each bundle auditable by reading it, and
  // what lets a future route-scoped loader drop two of the three.
  // `shell` is declared because this package READS it: the launcher tiles, the
  // tile context menu and the default navbar labels are shell chrome whose
  // strings live in the shared package's own bundle (57 launcher keys, 7
  // `shell.app.*`, 6 tile-menu keys). They load today only because `shell` is
  // in bootShell's BOOT_NAMESPACES; naming it here makes the dependency
  // DECLARED rather than incidental, so a future route-scoped loader cannot
  // drop it and leave those labels rendering raw keys.
  i18nNamespaces: [
    'common',
    'account',
    'settings',
    'workspace',
    'admin',
    'data',
    'shell',
    // `ai` — the assistant's chrome that the shell's OWN composer mount
    // (app.js) names: the Page-mode placeholder and the PAGE mode word are the
    // same strings the shared AI settings panel's "Answers" choice shows
    // (ASSISTANT_BOARD_MODE_SPEC §1, §4) — one word per concept on every
    // surface, never a second copy under `workspace.*`.
    'ai',
    // `members` — the header's invite panel (components/InviteDropdown.js, the
    // shell's layout of the shared invite parts) names the same invite copy
    // the shared parts render: title, role names, sent / copied lines, the
    // magic-link revoke words. One string per concept, never a second copy
    // under `shell.*`.
    'members',
    'tickets',
    'flags',
    // The five apps the shell routes but that carry no manifest of their own
    // (chat, meet, calendar, documents, files live in their own packages with
    // no `manifest.js`). Their bundles exist and the registry has them; without
    // this line bootShell loads none of the five and every one of those
    // surfaces renders English inside a Georgian session.
    'chat',
    // `mail` — the built-in email client (architecture/MAIL.md), routed here
    // as `/mail` with no manifest of its own, exactly like the five above.
    'mail',
    'meet',
    'calendar',
    'documents',
    'files',
    // `marketplace` is declared because this shell's own CHROME resolves store
    // copy on EVERY route, not because it routes `/marketplace`. An installed
    // app's navbar tab and launcher tile carry the app's identity, and their
    // label is resolved through the ONE store-copy resolver
    // (`shared/functions/storeItemText.js` → `marketplace.app.<slug>.name`), so
    // an app has the same name in the navbar as in the store it was installed
    // from. Undeclared, those 15 tabs printed their manifest's English literal
    // inside a Georgian session — "Warehouse" against the store's "ინვენტარი".
    // It is in bootShell's BOOT_NAMESPACES too: the navbar paints before the
    // per-route widening runs, so the manifest line alone would still flash
    // English. Cost, measured: the ka chunk is 51 KB against the 573 KB a
    // Georgian shell boot already loads.
    'marketplace',
    // `analytics` — /analytics, /workspace-analytics and the logs suite. The
    // package is a lazily merged shared library with a manifest of its own, but
    // its pages also open in a PageSlot pane from ANY route, where no route
    // match can add the namespace; and its own manifest is matched against the
    // browser path only at boot. Declared here, the dashboards read Georgian
    // wherever they are opened. Until 2026-09-21 the package had no bundle at
    // all and every one of its ~250 strings was an English literal.
    'analytics',
    // `infra` — /infra and /infra/status. The package is a HARD LEAF that
    // this shell's own `pages/index.js` loads through `lazySharedLib`; it
    // carries no manifest of its own, so this line is the only place that can
    // declare its bundle. `/infra/status` also opens in a CanvasPageSlot pane
    // from the infra rail, where no route match could widen the set. Until
    // 2026-09-22 the package had no bundle at all and every one of its
    // strings was an English literal.
    'infra',
    // `preview` — the `site-preview` AI-answer view (preview/components/
    // SitePreviewView.js) mounts in the assistant's thread and on a Fly page on
    // ANY route, and the /preview pane opens in a PageSlot from any route too;
    // its own manifest is matched against the browser path only at boot. 65
    // keys; declared here, both read Georgian wherever they open.
    'preview',
    // `canvas` — the `canvas-projects` AI-answer view (canvas/components/
    // CanvasProjectsView.js, its provider in the canvas eager slice) mounts in
    // the assistant's thread and on a Fly page on ANY route, and the home's own
    // canvas chrome (the composer's canvas strings) reads it too. Without it a
    // Georgian session shows those lines in English. The dependency is
    // declared, never copied into `workspace.*` (the ownership gate's rule).
    'canvas'
  ],
  app: () => import('./app.js').then((m) => m.default),
  context: () => import('./context.js').then((m) => m.default)
  // A `fetchOnMount: ({ state, sdk }) => sdk.workspaceProject?.notifications
  // ?.unreadCount()?.then(n => { state.notificationsUnread = n })` hook used
  // to live here. Removed 2026-07-27 (Rule 63 sweep handoff): `fetchOnMount`
  // is a Phase-8 manifest convention honored only by bootShell's single-app
  // standalone dispatch (`_singleAppDispatch`/`isSingleApp` in
  // shared/functions/bootShell.js) — this shell boots as a MULTI-app shell
  // (`index.js` passes 4 manifests + a pre-built context, so
  // `isSingleApp = false`), and neither `dispatch.js` nor
  // `wireGlobalListeners.js` ever call a manifest's `fetchOnMount` — so this
  // write NEVER actually ran outside its own unit test. `notificationsUnread`
  // has zero live readers inside this shell either way:
  // `shared/components/NotificationBell.js` is the only consumer of that
  // state key anywhere in the monorepo, and it's shadowed here by
  // `workspace/components/NotificationBell.js` (which reads the unrelated
  // `root.notifs` array instead) — it only actually mounts in canvas's
  // standalone build, which doesn't import this manifest. A dead writer
  // feeding a key nobody in this shell reads; see tickets/workspace.md
  // Rule 63 entry (#6).
}
